import { useCallback, useEffect, useState } from 'react';
import type { NavMenuEntry } from '../navigation/navItems';

/**
 * Checks if the given pathname matches the given nav target.
 */
export function pathMatchesNavTarget(pathname: string, to: string): boolean {
  if (to === '/') {
    return pathname === '/';
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Returns the expanded group labels for the given pathname and nav entries.
 */
export function useNavGroupsExpandedForPath(
  pathname: string,
  navEntries: NavMenuEntry[],
): {
  expandedGroupLabels: ReadonlySet<string>;
  toggleNavGroup: (label: string) => void;
} {
  const [expandedNavGroups, setExpandedNavGroups] = useState<Set<string>>(() => new Set());

  const toggleNavGroup = useCallback((label: string) => {
    setExpandedNavGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedNavGroups((prev) => {
      const next = new Set(prev);
      for (const e of navEntries) {
        if (e.type !== 'group') {
          continue;
        }
        if (e.children.some((c) => pathMatchesNavTarget(pathname, c.to))) {
          next.add(e.label);
        }
      }
      return next;
    });
  }, [pathname, navEntries]);

  return { expandedGroupLabels: expandedNavGroups, toggleNavGroup };
}
